import React, { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { Loader2, FileText, Check, Download, Info, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/* ════ MAIN ════ */
export default function SOWView({ tenantId }) {
  const [sow, setSow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");

  const fetchSOW = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sow/tenant/${tenantId}`);
      if (res.ok) setSow(await res.json());
    } catch {}
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { fetchSOW(); }, [fetchSOW]);

  if (loading)
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#9ca3af]" />
      </div>
    );

  if (!sow)
    return (
      <div className="rounded-[14px] border border-[#e5e7eb] bg-white p-12 text-center">
        <div className="h-14 w-14 rounded-full bg-[#eff6ff] flex items-center justify-center mx-auto mb-4">
          <FileText className="h-7 w-7 text-[#1a56db]" />
        </div>
        <h4 className="text-[16px] font-bold text-[#111827] mb-2">No Scope of Services yet</h4>
        <p className="text-[13px] text-[#9ca3af] max-w-md mx-auto leading-[1.6]">
          Your Scope of Services will appear here once 589 Labs prepares it for your review.
        </p>
      </div>
    );

  const isApproved = sow.status === "approved";
  const canApprove = sow.status === "sent_to_client";
  const workflows = sow.workflows || [];
  const total = sow.total_amount != null
    ? sow.total_amount
    : (sow.base_fee || 0) + (sow.additional_workflow_count || 0) * (sow.additional_workflow_unit_price || 0);

  // Status pill text + colors per screenshot
  const statusPill = (() => {
    if (isApproved) return { bg: "#ecfdf5", fg: "#059669", label: "Approved", icon: "✓" };
    if (canApprove) return { bg: "#eff6ff", fg: "#1a56db", label: "Awaiting Your Approval", icon: "⏳" };
    if (sow.status === "internal_review") return { bg: "#fffbeb", fg: "#b45309", label: "Under Review", icon: "⧗" };
    return { bg: "#f3f4f6", fg: "#6b7280", label: "Draft", icon: "•" };
  })();

  const TABS = [
    { key: "summary", label: "Summary" },
    { key: "workflows", label: "Workflows" },
    { key: "commercial", label: "Commercial" },
    { key: "terms", label: "Details & Terms" },
    { key: "approval", label: "Approval" },
  ];

  return (
    <div className="space-y-0" data-testid="sow-view">
      {/* ═══ SOW HEADER CARD ═══ */}
      <div className="rounded-[14px] border border-[#e5e7eb] bg-white p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-[22px] font-bold text-[#111827] tracking-[-0.01em] flex items-center gap-2">
              <span>📄</span>Scope of Services
            </h1>
            <span
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-3 py-1 rounded-full"
              style={{ backgroundColor: statusPill.bg, color: statusPill.fg }}
              data-testid="sow-status-pill"
            >
              <span>{statusPill.icon}</span>
              {statusPill.label}
            </span>
          </div>
          <Button
            variant="outline"
            onClick={() => window.open(`${API}/api/sow/tenant/${tenantId}/pdf`, "_blank")}
            className="h-9 rounded-[8px] border-[#e5e7eb] bg-white text-[#374151] text-[13px] font-semibold hover:bg-[#f9fafb]"
            data-testid="download-signed-pdf"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />Export PDF
          </Button>
        </div>

        <p className="text-[13px] text-[#6b7280] mb-5">
          {sow.sow_number} · <span className="font-semibold text-[#111827]">v{sow.version}</span> · {sow.phase_name}
        </p>

        <div className="border-t border-[#e5e7eb] pt-5 grid gap-6 sm:grid-cols-2">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9ca3af] mb-2">Consultant</div>
            <div className="text-[15px] font-bold text-[#111827]">589 Labs</div>
            <div className="text-[13px] text-[#6b7280] mt-0.5">AI Strategy &amp; Implementation</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9ca3af] mb-2">Client</div>
            <div className="text-[15px] font-bold text-[#111827]">{sow.client_legal_name || "—"}</div>
            {sow.client_primary_contact && (
              <div className="text-[13px] text-[#6b7280] mt-0.5">{sow.client_primary_contact}</div>
            )}
            {sow.client_email && (
              <div className="text-[13px] text-[#6b7280] mt-0.5">{sow.client_email}</div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ TABS ═══ */}
      <div className="flex items-center gap-6 border-b border-[#e5e7eb] overflow-x-auto mb-6">
        {TABS.map((t) => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`pb-3 pt-1 text-[14px] font-semibold border-b-2 transition-colors whitespace-nowrap ${
                active ? "border-[#1a56db] text-[#1a56db]" : "border-transparent text-[#6b7280] hover:text-[#111827]"
              }`}
              data-testid={`sow-nav-${t.key}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ═══ TAB CONTENT ═══ */}
      {activeTab === "summary" && (
        <div className="space-y-6">
          <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-6">
            <h2 className="text-[17px] font-bold text-[#111827] mb-3 tracking-[-0.005em]">Engagement Summary</h2>
            <p className="text-[14px] leading-[1.7] text-[#374151]">
              {sow.short_form_sections?.engagement_summary}
            </p>
          </div>

          {/* 2x2 grid with emoji icons */}
          <div className="grid gap-5 sm:grid-cols-2">
            <SummaryCard
              icon="📦"
              title="What's Included"
              body={sow.short_form_sections?.what_is_included}
            />
            <SummaryCard
              icon="🤝"
              title="Client Responsibilities"
              body={sow.short_form_sections?.client_responsibilities}
            />
            <SummaryCard
              icon="🎯"
              title="Scope Control"
              body={
                sow.short_form_sections?.scope_control ||
                "Only the workflows listed in this SOW are in scope. Any expanded scope, additional workflows, or new requirements beyond this agreement require a formal change order, add-on workflow agreement, or retainer engagement."
              }
            />
            <SummaryCard
              icon="⏱"
              title="Timeline & Delivery"
              body={
                sow.short_form_sections?.timeline_delivery ||
                "Work begins within five (5) business days of SOW approval. Estimated duration is 8–12 weeks depending on workflow complexity and client availability. Services delivered remotely by default."
              }
            />
          </div>
        </div>
      )}

      {activeTab === "workflows" && (
        <div className="space-y-5">
          {workflows.map((wf, i) => {
            const inc = wf.workflow_type === "included";
            return (
              <div
                key={wf.id}
                className="rounded-[12px] border border-[#e5e7eb] bg-white overflow-hidden border-l-[3px] border-l-[#1a56db]"
                data-testid={`sow-workflow-${i}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3 mb-5">
                    <h3 className="text-[18px] font-bold text-[#111827] tracking-[-0.005em]">{wf.workflow_name}</h3>
                    <span
                      className="shrink-0 inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1 rounded-full"
                      style={
                        inc
                          ? { backgroundColor: "#ecfdf5", color: "#059669" }
                          : { backgroundColor: "#fffbeb", color: "#b45309" }
                      }
                    >
                      {inc ? "✓ Included" : "+ Additional"}
                    </span>
                  </div>

                  {wf.business_problem && (
                    <div className="mb-5">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9ca3af] mb-1.5">Business Problem</div>
                      <p className="text-[14px] leading-[1.65] text-[#374151]">{wf.business_problem}</p>
                    </div>
                  )}

                  <div className="grid gap-5 sm:grid-cols-2">
                    {wf.objective && (
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9ca3af] mb-1.5">Objective</div>
                        <p className="text-[14px] leading-[1.65] text-[#374151]">{wf.objective}</p>
                      </div>
                    )}
                    {wf.success_metric && (
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9ca3af] mb-1.5">Success Metric</div>
                        <p className="text-[14px] leading-[1.65] text-[#374151]">{wf.success_metric}</p>
                      </div>
                    )}
                    {wf.deliverables && (
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9ca3af] mb-1.5">Deliverables</div>
                        <p className="text-[14px] leading-[1.65] text-[#374151]">{wf.deliverables}</p>
                      </div>
                    )}
                    {wf.assumptions && (
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9ca3af] mb-1.5">Assumptions</div>
                        <p className="text-[14px] leading-[1.65] text-[#374151]">{wf.assumptions}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {workflows.length === 0 && (
            <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-10 text-center text-[14px] text-[#6b7280]">
              No workflows defined yet.
            </div>
          )}
        </div>
      )}

      {activeTab === "commercial" && (
        <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-6">
          <h2 className="text-[17px] font-bold text-[#111827] tracking-[-0.005em] mb-6">Commercial Summary</h2>

          <div className="flex items-center justify-between py-2">
            <span className="text-[14px] text-[#374151]">
              Phase 1 Base Fee ({sow.included_workflow_count} workflows included)
            </span>
            <span className="text-[15px] font-bold text-[#111827]">${(sow.base_fee || 0).toLocaleString()}</span>
          </div>

          {(sow.additional_workflow_count || 0) > 0 && (
            <div className="flex items-center justify-between py-2 border-t border-[#f3f4f6]">
              <span className="text-[14px] text-[#374151]">
                Additional Workflows ({sow.additional_workflow_count} × ${(sow.additional_workflow_unit_price || 0).toLocaleString()})
              </span>
              <span className="text-[15px] font-bold text-[#111827]">
                ${((sow.additional_workflow_count || 0) * (sow.additional_workflow_unit_price || 0)).toLocaleString()}
              </span>
            </div>
          )}

          <div className="border-t border-[#e5e7eb] mt-4 pt-4 flex items-end justify-between">
            <span className="text-[20px] font-bold text-[#111827]">Estimated Total</span>
            <span className="text-[32px] font-bold text-[#1a56db] leading-none">${total.toLocaleString()}</span>
          </div>

          <div className="grid gap-6 sm:grid-cols-3 mt-8 pt-6 border-t border-[#e5e7eb]">
            <div>
              <div className="text-[12px] font-medium text-[#9ca3af] mb-1">Delivery Model</div>
              <div className="text-[14px] font-semibold text-[#111827]">Remote (default)</div>
            </div>
            <div>
              <div className="text-[12px] font-medium text-[#9ca3af] mb-1">Onsite Rate</div>
              <div className="text-[14px] font-semibold text-[#111827]">${sow.onsite_hourly_rate}/hr + travel</div>
            </div>
            <div>
              <div className="text-[12px] font-medium text-[#9ca3af] mb-1">Payment Terms</div>
              <div className="text-[14px] font-semibold text-[#111827]">{sow.payment_terms}</div>
            </div>
          </div>

          <p className="text-[12px] italic text-[#9ca3af] mt-5">
            Retainer plans are available separately upon project completion.
          </p>
        </div>
      )}

      {activeTab === "terms" && (
        <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-6 md:p-8" data-testid="sow-terms-section">
          <h2 className="text-[17px] font-bold text-[#111827] tracking-[-0.005em] mb-6">Terms &amp; Conditions</h2>
          {sow.legal_clauses && Object.keys(sow.legal_clauses).length > 0 ? (
            <div className="grid gap-x-8 gap-y-7 sm:grid-cols-2">
              {Object.entries(sow.legal_clauses).map(([key, text]) => (
                <div key={key}>
                  <h3 className="text-[15px] font-bold text-[#111827] mb-2 pb-2 border-b border-[#f3f4f6] capitalize">
                    {key.replace(/_/g, " ")}
                  </h3>
                  <p className="text-[13px] leading-[1.65] text-[#4b5563]">{text}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[13px] leading-[1.65] text-[#4b5563]">
              Terms &amp; Conditions will be attached to the final signed PDF. For any clarifications, please contact your 589 Labs consultant.
            </p>
          )}

          {/* D-09 (May-2026) — Third-Party AI Tool Costs disclaimer */}
          <div className="mt-7 pt-6 border-t border-[#f3f4f6]" data-testid="sow-third-party-ai-disclaimer">
            <h3 className="text-[15px] font-bold text-[#111827] mb-2">Third-Party AI Tool Costs</h3>
            <p className="text-[13px] leading-[1.65] text-[#4b5563]">
              Third-party AI tools — including large language models (LLMs), agent
              builders, and related AI infrastructure — are <strong>not included</strong> in
              these service fees. These costs are based entirely on token usage and are
              billed directly to the client by the AI provider (e.g. Anthropic,
              OpenAI). 589 Labs will make best efforts to minimize token consumption
              through efficient prompt design and model selection, but cannot
              guarantee or control third-party pricing. A usage estimate will be
              provided as part of the implementation plan.
            </p>
          </div>
        </div>
      )}

      {activeTab === "approval" && (
        <div>
          {isApproved ? (
            <ApprovedState sow={sow} tenantId={tenantId} />
          ) : canApprove ? (
            <ApprovalForm tenantId={tenantId} sow={sow} onApproved={fetchSOW} />
          ) : (
            <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-12 text-center">
              <div className="h-14 w-14 rounded-full bg-[#f3f4f6] flex items-center justify-center mx-auto mb-4">
                <Lock className="h-6 w-6 text-[#9ca3af]" />
              </div>
              <h4 className="text-[16px] font-bold text-[#111827] mb-2">Not ready for approval yet</h4>
              <p className="text-[13px] text-[#9ca3af] max-w-sm mx-auto leading-[1.6]">
                This Scope of Services will become available once 589 Labs sends it to you for review.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ════ SUMMARY CARD (emoji icon block) ════ */
const SummaryCard = ({ icon, title, body }) => (
  <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-6">
    <div className="text-[22px] mb-4 leading-none">{icon}</div>
    <h3 className="text-[15px] font-bold text-[#111827] mb-3 tracking-[-0.005em]">{title}</h3>
    <p className="text-[13px] leading-[1.65] text-[#4b5563]">{body}</p>
  </div>
);

/* ════ APPROVED STATE ════ */
function ApprovedState({ sow, tenantId }) {
  const sig = sow.signature;
  return (
    <div className="rounded-[14px] border border-[#a7f3d0] bg-[#ecfdf5] p-6" data-testid="sow-approved-state">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center shrink-0">
          <Check className="h-6 w-6 text-[#059669]" strokeWidth={3} />
        </div>
        <div>
          <h2 className="text-[18px] font-bold text-[#111827] tracking-[-0.01em]">Scope of Services Approved</h2>
          <p className="text-[12px] text-[#059669] mt-0.5">Your engagement is officially underway</p>
        </div>
      </div>
      {sig && (
        <div className="grid gap-3 sm:grid-cols-2 mt-4 text-[13px]">
          {[
            ["Signed by", sig.signer_full_name],
            ["Title", sig.signer_role],
            ["Email", sig.signer_email],
            ["Approved", new Date(sig.signed_at).toLocaleString()],
          ].map(([l, v]) => (
            <div key={l} className="rounded-[10px] bg-white border border-[#a7f3d0] p-3">
              <div className="text-[10px] font-semibold text-[#059669] uppercase tracking-wider mb-0.5">{l}</div>
              <div className="text-[13px] font-semibold text-[#111827]">{v}</div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-5">
        <Button
          variant="outline"
          onClick={() => window.open(`${API}/api/sow/tenant/${tenantId}/pdf`, "_blank")}
          className="h-9 rounded-[8px] bg-white border-[#a7f3d0] text-[#059669] text-[12px] font-semibold hover:bg-[#ecfdf5]"
          data-testid="download-signed-pdf-bottom"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />Download Signed PDF
        </Button>
      </div>
    </div>
  );
}

/* ════ APPROVAL FORM — matches batch 3 spec precisely ════ */
function ApprovalForm({ tenantId, sow, onApproved }) {
  const [form, setForm] = useState({
    signer_full_name: sow?.client_legal_name || sow?.client_company || "",
    signer_role: sow?.client_primary_contact ? `${sow.client_primary_contact}${sow.client_title ? `, ${sow.client_title}` : ""}` : "",
    signer_email: sow?.client_email || "",
  });
  const [checks, setChecks] = useState({ authority: false, esign: false, terms: false });
  const [submitting, setSubmitting] = useState(false);
  const todayStr = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const allChecked = checks.authority && checks.esign && checks.terms;
  const allFilled = form.signer_full_name && form.signer_role && form.signer_email;
  const canSubmit = allChecked && allFilled && !submitting;

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/sow/tenant/${tenantId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signer_full_name: form.signer_full_name,
          signer_role: form.signer_role,
          signer_email: form.signer_email,
          agreed_authority: checks.authority,
          agreed_esign: checks.esign,
          agreed_terms: checks.terms,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Approval failed");
      }
      toast.success("Scope of Services Approved! A signed PDF will be emailed to you.");
      onApproved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "h-11 rounded-[8px] border-[#e5e7eb] bg-white text-[#111827] text-[14px] placeholder:text-[#9ca3af] focus-visible:ring-2 focus-visible:ring-[#1a56db]/20 focus-visible:border-[#1a56db]";
  const labelClass = "block text-[13px] font-medium text-[#111827] mb-2";

  const CONSENTS = [
    { key: "authority", label: "I confirm that I am authorized to bind the Client to the terms of this Scope of Services." },
    { key: "esign", label: "I consent to the use of electronic records and electronic signatures for this transaction." },
    { key: "terms", label: "I have read and agree to the terms and conditions of this Scope of Services." },
  ];

  return (
    <div
      className="rounded-[14px] border border-[#e5e7eb] bg-white p-8"
      data-testid="sow-approval-form"
    >
      <h2 className="text-[20px] font-bold text-[#111827] tracking-[-0.01em] mb-2">Approve Scope of Services</h2>
      <p className="text-[13px] text-[#6b7280] mb-7 flex items-center gap-1.5">
        <Info className="h-3.5 w-3.5 text-[#1a56db]" />
        Complete the fields below to electronically approve this Scope of Services.
      </p>

      <div className="grid gap-5 sm:grid-cols-2 mb-6">
        <div>
          <label className={labelClass}>Full Legal Name of Client</label>
          <Input
            value={form.signer_full_name}
            onChange={(e) => setForm({ ...form, signer_full_name: e.target.value })}
            placeholder="Acme Corporation"
            className={inputClass}
            data-testid="signer-name"
          />
        </div>
        <div>
          <label className={labelClass}>Your Full Name &amp; Title</label>
          <Input
            value={form.signer_role}
            onChange={(e) => setForm({ ...form, signer_role: e.target.value })}
            placeholder="John Smith, CTO"
            className={inputClass}
            data-testid="signer-role"
          />
        </div>
        <div>
          <label className={labelClass}>Your Email</label>
          <Input
            type="email"
            value={form.signer_email}
            onChange={(e) => setForm({ ...form, signer_email: e.target.value })}
            placeholder="john@acme.com"
            className={inputClass}
            data-testid="signer-email"
          />
        </div>
        <div>
          <label className={labelClass}>Date of Approval</label>
          <Input
            value={todayStr}
            readOnly
            className="h-11 rounded-[8px] border-[#e5e7eb] bg-[#f9fafb] text-[#6b7280] text-[14px] cursor-default"
            data-testid="signer-date"
          />
        </div>
      </div>

      {/* Consent card-checkboxes */}
      <div className="space-y-3 mb-6">
        {CONSENTS.map((c) => {
          const isChecked = checks[c.key];
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setChecks({ ...checks, [c.key]: !isChecked })}
              className={`w-full flex items-center gap-3 px-5 py-4 rounded-[10px] border text-left transition-all ${
                isChecked ? "border-[#a7f3d0] bg-[#ecfdf5]" : "border-[#e5e7eb] bg-white hover:border-[#bfdbfe] hover:bg-[#f9fafb]"
              }`}
              data-testid={`check-${c.key}`}
            >
              <div
                className={`h-5 w-5 rounded-[6px] border-2 flex items-center justify-center shrink-0 transition-all ${
                  isChecked ? "bg-[#10b981] border-[#10b981]" : "bg-white border-[#d1d5db]"
                }`}
              >
                {isChecked && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
              </div>
              <span className={`text-[14px] leading-[1.5] ${isChecked ? "text-[#111827]" : "text-[#374151]"}`}>
                {c.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Full-width blue Approve button */}
      <Button
        onClick={handleApprove}
        disabled={!canSubmit}
        className="h-14 w-full rounded-[10px] bg-[#1a56db] text-white text-[15px] font-bold hover:bg-[#143faa] disabled:bg-[#1a56db]/60 disabled:cursor-not-allowed shadow-none"
        data-testid="approve-sow-btn"
      >
        {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" strokeWidth={3} />}
        Approve Scope of Services
      </Button>

      {!allChecked && (
        <p className="mt-3 text-[12px] text-[#9ca3af] text-center">
          All three consent items must be checked to proceed
        </p>
      )}
    </div>
  );
}
